import { Injectable, Injector } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { finalize } from 'rxjs/operators';
// import { AddUserLabelApiRequest } from '@aiii/aiii-types/product/label/add-user-label-api-request';
// import { AddUserLabelApiResponse } from '@aiii/aiii-types/product/label/add-user-label-api-response';
// import { SetSiteLabelApiRequest } from '@aiii/aiii-types/product/label/set-site-label-api-request';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom, firstValueFrom } from 'rxjs';
import liff from '@line/liff';
import { environment } from '../../environments/environment';
import { LiffConfig } from './interfaces/liff-config';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  public progress = 0;

  constructor(public storage: AngularFireStorage, private http: HttpClient) {}

  public async sleep(microsecond: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, microsecond);
    });
  }

  /*
  上傳檔案至firebase storage

  file(必要):檔案
  folder(必要):storage的目錄
  filename(可選):上傳後的檔案名稱
   */
  public uploadFile(
    file: File,
    folder: string,
    filename?: string
  ): Promise<{
    downloadURL: string;
    metadata: {
      size: number;
      updated: string;
      contentType: string;
      bucket: string;
      type: string;
      fullPath: string;
    };
  }> {
    return new Promise((res, rej) => {
      const fileNmae = filename ? filename : Date.now().toString() + '-' + file.name;
      const task = this.storage.upload(`/${folder}/${fileNmae}`, file);
      const fileRef = this.storage.ref(`/${folder}/${fileNmae}`);
      task.percentageChanges().subscribe((snapshot) => {
        this.progress = parseFloat(snapshot!.toFixed(2));
      });

      task
        .snapshotChanges()
        .pipe(
          finalize(() => {
            fileRef.getDownloadURL().subscribe((downloadURL) => {
              fileRef.getMetadata().subscribe((Metadata) => {
                res({
                  downloadURL,
                  metadata: Metadata,
                });
              });
            });
          })
        )
        .subscribe();
    });
  }

  /*
   * 移除storage中的檔案
   * @param path 檔案路徑
   */
  public removeFile(path: string) {
    return Promise.resolve(this.storage.ref(path).delete());
  }

  /**
   * 新增用戶標籤 v2
   * @param site 站台
   * @param userId 用戶ID
   * @param labels 標籤列表
   * @param timeliness:  是否為時效標籤, 否的話則不會刷新此標籤的最後更新時間
   * @param ignoreTimelinessMode 依照站台是否有啟用進階版的時效標籤功能設置 若沒有購買則為 True
   * @example
   *    const result = await this.unitLabelService.addUserLabelV2('aiii', 'U88bbbfb8c8b419f9571a1151237159e9', ['標籤1', '標籤2'], false, false);
   *    console.log(result); // {"status": "success"}
   */
  // public async addUserLabel(
  //   site: AddUserLabelApiRequest['site'],
  //   userId: AddUserLabelApiRequest['userId'],
  //   labels: AddUserLabelApiRequest['labels'],
  //   timeliness: SetSiteLabelApiRequest['timeliness'],
  //   ignoreTimelinessMode: AddUserLabelApiRequest['ignoreTimelinessMode']
  // ) {
  //   if (labels.length > 0) {
  //     const body: AddUserLabelApiRequest = {
  //       site,
  //       userId,
  //       labels,
  //       timeliness,
  //       ignoreTimelinessMode,
  //     };
  //     return (await this.http
  //       .post(`${environment.cloudrun.label}/add-user-label`, body)
  //       .toPromise()
  //       .catch((e) => alert(e.message))) as AddUserLabelApiResponse;
  //   }
  // }

  /**
   * 下載 excel
   * @param columns 標題名稱
   * @param rows 欄位內容
   * @param fileName 檔案名稱
   * @param titleWidth 欄位寬度
   * @param sheetName 試算表名稱
   */
  public async downloadExcel(
    titles: string[],
    rows: any[][],
    fileName: string,
    titleWidth: number[] = [],
    sheetName: string = '工作表'
  ): Promise<void> {
    const result: { fileUrl: string } = (await lastValueFrom(
      this.http.post(`https://stage-internal-api.aiii.ai/label/download-xlsx`, {
        title: titles,
        title_width: titleWidth,
        data: rows,
        fileName,
        sheetName,
      })
    )) as { fileUrl: string };
    liff.openWindow({
      url: result.fileUrl,
      external: true,
    });
  }

  /**
   * 取得下載excel的url
   * @param columns 標題名稱
   * @param rows 欄位內容
   * @param fileName 檔案名稱
   * @param titleWidth 欄位寬度
   * @param sheetName 試算表名稱
   */
  public async getDownloadExcelUrl(
    titles: string[],
    rows: any[][],
    fileName: string,
    titleWidth: number[] = [],
    sheetName: string = '工作表'
  ): Promise<string> {
    const result: { fileUrl: string } = (await lastValueFrom(
      this.http.post(`https://stage-internal-api.aiii.ai/label/download-xlsx`, {
        title: titles,
        title_width: titleWidth,
        data: rows,
        fileName,
        sheetName,
      })
    )) as { fileUrl: string };

    return result.fileUrl;
  }

  /**
   * ajax方法
   * @param url
   * @param data
   */
  httpClientPostBlob(url: string, formData: FormData, apikey?: string) {
    let headers = new HttpHeaders();
    if (apikey) {
      headers = headers.append('x-api-key', apikey);
    }

    return lastValueFrom(this.http.post<Blob>(url, formData, { headers: headers, responseType: 'json' }));
  }

  /**
   * ajax方法
   * @param url
   * @param data
   */
  ajaxJsonPost<T = any>(url: string, data: any, apikey?: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=UTF-8',
    });
    if (apikey) {
      headers = headers.append('x-api-key', apikey);
    }
    return firstValueFrom(this.http.post<T>(url, data, { observe: 'body', headers: headers }));
  }

  /**
   * 查詢該 site 站台的 botId 、Liff ID 及 公開資料
   * @param site 站台名稱
   */
  async querySiteProfile(site: string): Promise<{
    site: string;
    botId: string;
    displayName: string;
    liff: LiffConfig;
    accountId: string;
    provider: string;
  }> {
    const result: any = await this.http.post(`${environment.cloudrun.apiv2}/querySiteConfig`, { site }).toPromise();
    return result;
  }

  /**
   * 判斷是否為原生LIFF的環境
   */
  public isInClient() {
    try {
      return liff.isInClient();
    } catch (err) {
      return false;
    }
  }
}
